import { StandardFonts, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import checkPngUrl from "../assets/check.png";
import closePngUrl from "../assets/close.png";
import circlePngUrl from "../assets/circle.png";
import { formatOldUrl } from "./cloudApi";

// 1) Friendly name → TTF URLs
const fontUrls = {
  arial: "/fonts/arial/arial.ttf",
  arialBold: "/fonts/arial/arial-bold.ttf",
  arialItalic: "/fonts/arial/arial-italic.ttf",
  arialBoldItalic: "/fonts/arial/arial-bold-italic.ttf",
  comic: "/fonts/comic/comic.ttf",
  comicBold: "/fonts/comic/comic-bold.ttf",
  comicItalic: "/fonts/comic/comic-italic.ttf",
  comicBoldItalic: "/fonts/comic/comic-bold-italic.ttf",
  courier: "/fonts/courier/courier.ttf",
  courierBold: "/fonts/courier/courier-bold.ttf",
  courierItalic: "/fonts/courier/courier-italic.ttf",
  courierBoldItalic: "/fonts/courier/courier-bold-italic.ttf",
  opensans: "/fonts/opensans/opensans.ttf",
  opensansBold: "/fonts/opensans/opensans-bold.ttf",
  opensansItalic: "/fonts/opensans/opensans-italic.ttf",
  opensansBoldItalic: "/fonts/opensans/opensans-bold-italic.ttf",
  roboto: "/fonts/roboto/roboto.ttf",
  robotoBold: "/fonts/roboto/roboto-bold.ttf",
  robotoItalic: "/fonts/roboto/roboto-italic.ttf",
  robotoBoldItalic: "/fonts/roboto/roboto-bold-italic.ttf",
  times: "/fonts/times/times.ttf",
  timesBold: "/fonts/times/times-bold.ttf",
  timesItalic: "/fonts/times/times-italic.ttf",
  timesBoldItalic: "/fonts/times/times-bold-italic.ttf",
  verdana: "/fonts/verdana/verdana.ttf",
  verdanaBold: "/fonts/verdana/verdana-bold.ttf",
  verdanaItalic: "/fonts/verdana/verdana-italic.ttf",
  verdanaBoldItalic: "/fonts/verdana/verdana-bold-italic.ttf",
};

// 2) Fetch TTF as ArrayBuffer
async function fetchFontBuffer(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load font ${url}: ${res.status}`);
  return res.arrayBuffer();
}

// 3) Embed all your TTFs
async function loadCustomFonts(pdfDoc) {
  pdfDoc.registerFontkit(fontkit);
  const entries = await Promise.all(
    Object.entries(fontUrls).map(async ([key, url]) => {
      const buf = await fetchFontBuffer(url);
      const font = await pdfDoc.embedFont(buf);
      return [key, font];
    })
  );
  return Object.fromEntries(entries);
}

// 4) Better CSS-to-RGB parser
function parseColor(css) {
  if (!css || css.trim().toLowerCase() === "transparent") return null;
  css = css.trim();
  if (css.startsWith("#")) {
    let hex = css.slice(1);
    if (hex.length === 3)
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    if (hex.length === 8) hex = hex.slice(0, 6);
    if (/^[0-9A-Fa-f]{6}$/.test(hex)) {
      const [r, g, b] = hex.match(/.{2}/g).map((h) => parseInt(h, 16) / 255);
      return rgb(r, g, b);
    }
    return rgb(0, 0, 0);
  }
  const nums = css.match(/(\d+(\.\d+)?%?)/g);
  if (nums && nums.length >= 3) {
    const to255 = (v) =>
      v.endsWith("%") ? parseFloat(v) / 100 : parseFloat(v) / 255;
    const [r, g, b] = nums.slice(0, 3).map(to255);
    return rgb(r, g, b);
  }
  return rgb(0, 0, 0);
}

export const getImageDataUrlFromServer = async (imageUrl) => {
  let tempUrl = imageUrl;
  if (imageUrl.startsWith("https://uphub-app.tier-app.com")) {
    tempUrl = formatOldUrl(imageUrl);
  }

  const res = await fetch(tempUrl);
  if (!res.ok)
    throw new Error(`Failed to load image at ${tempUrl}: ${res.status}`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

export async function embedFields(pdfDoc, fields, ptPerPx = 1, debug = false) {
  // a) load/embed your custom fonts
  const customFonts = await loadCustomFonts(pdfDoc);

  // b) embed fallback StandardFonts
  const stdDefs = {
    helvetica: StandardFonts.Helvetica,
    helveticaBold: StandardFonts.HelveticaBold,
    helveticaOblique: StandardFonts.HelveticaOblique,
    helveticaBoldOblique: StandardFonts.HelveticaBoldOblique,
  };
  const fallbackFonts = {};
  for (const [k, stdName] of Object.entries(stdDefs)) {
    fallbackFonts[k] = await pdfDoc.embedFont(stdName);
  }

  // c) build a case-insensitive font map
  const fontMap = {};
  for (const [key, font] of Object.entries(customFonts)) {
    fontMap[key.toLowerCase()] = font;
    fontMap[font.name.toLowerCase()] = font;
  }
  for (const [key, font] of Object.entries(fallbackFonts)) {
    fontMap[key.toLowerCase()] = font;
    fontMap[font.name.toLowerCase()] = font;
  }
  const defaultFont = fallbackFonts.helvetica;

  // d) draw each field
  const pages = pdfDoc.getPages();
  for (const f of fields) {
    const page = pages[(f.page || 1) - 1];
    if (!page) continue;
    const { height: pageHPt } = page.getSize();

    // Convert all your base metrics from px → pt:
    const xPt = (f.baseLeft ?? 0) * ptPerPx;
    const topPt = (f.baseTop ?? 0) * ptPerPx;
    const wPt = (f.width ?? f.baseWidth ?? 0) * ptPerPx;
    const hPt = (f.height ?? f.baseHeight ?? 0) * ptPerPx;
    const yPt = pageHPt - topPt - hPt;

    if (f.type === "textbox") {
      const {
        text = "",
        size = 12,
        textAlign = "left",
        color: textColorHex = "#000",
        fillColor: bgColorHex = "transparent",
        fontFamily = "arial",
        bold = false,
        italic = false,
      } = f;

      const fontSizePt = size * ptPerPx;

      const fontKey =
        fontFamily.toLowerCase() +
        (bold ? "bold" : "") +
        (italic ? "italic" : "");
      const font = fontMap[fontKey] || fontMap[fontFamily] || defaultFont;

      // if (debug) console.log(`Using font "${font.name}" for`, fontKey);

      const curYpt = yPt + hPt * 0.4;

      // Draw background rectangle at the original position
      if (parseColor(bgColorHex)) {
        page.drawRectangle({
          x: xPt,
          y: curYpt,
          width: wPt,
          height: hPt,
          color: parseColor(bgColorHex),
        });
      }

      // Approximate ascent
      let ascentPt =
        (font.ascent ?? font.capHeight ?? font.height) *
        (fontSizePt / (font.unitsPerEm || 1000));
      if (!ascentPt || isNaN(ascentPt)) {
        ascentPt = 0.8 * fontSizePt;
      }

      // Center the text vertically in the box
      const textY = curYpt + (hPt - fontSizePt) / 2 + fontSizePt * 0.25;

      page.drawText(text, {
        x: xPt,
        y: textY,
        size: fontSizePt,
        font,
        color: parseColor(textColorHex) || rgb(0, 0, 0),
        lineHeight: fontSizePt,
        maxWidth: wPt,
        wordBreaks: [" "],
        align: textAlign,
      });
    }

    if (f.type === "checkbox") {
      const style = (f.checkboxStyle || "check").toLowerCase();
      let assetUrl = checkPngUrl;
      if (style === "x") assetUrl = closePngUrl;
      else if (style === "circle") assetUrl = circlePngUrl;

      const imgBytes = await getImageDataUrlFromServer(assetUrl);
      const embeddedImg = await pdfDoc.embedPng(imgBytes);

      // scale down checkbox slightly to match UI
      const scale = 1;
      const imgW = wPt * scale;
      const imgH = hPt * scale;
      const imgX = xPt + (wPt - imgW) / 2;
      const imgY = yPt + (hPt - imgH) / 2;

      page.drawImage(embeddedImg, {
        x: imgX,
        y: imgY,
        width: imgW,
        height: imgH,
      });
    }

    if (f.type === "image" || f.type === "signature") {
      let bytes;
      if (f.data) bytes = f.data;
      else {
        const dataUrl = await getImageDataUrlFromServer(f.src);
        const b64 = dataUrl.split(",")[1];
        const bin = atob(b64);
        bytes = new Uint8Array(bin.length).map((_, i) => bin.charCodeAt(i));
      }
      const lower = (f.src || "").toLowerCase();
      const img = lower.endsWith(".png")
        ? await pdfDoc.embedPng(bytes)
        : await pdfDoc.embedJpg(bytes);

      page.drawImage(img, { x: xPt, y: yPt, width: wPt, height: hPt });
    }
  }

  return pdfDoc;
}
